import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "alati_cart_v1";

const readStored = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            return {
                items: Array.isArray(parsed.items) ? parsed.items : [],
                giftBox: Boolean(parsed.giftBox),
            };
        }
    } catch (err) {
        console.warn("ALATI: failed to restore cart from localStorage", err);
    }
    return { items: [], giftBox: false };
};

export const CartProvider = ({ children }) => {
    const initial = readStored();
    const [items, setItems] = useState(initial.items);
    const [giftBox, setGiftBox] = useState(initial.giftBox);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, giftBox }));
        } catch (err) {
            console.warn("ALATI: failed to persist cart", err);
        }
    }, [items, giftBox]);

    const add = (product, qty = 1) => {
        setItems((prev) => {
            const existing = prev.find((p) => p.slug === product.slug);
            if (existing) {
                return prev.map((p) =>
                    p.slug === product.slug ? { ...p, quantity: p.quantity + qty } : p,
                );
            }
            return [
                ...prev,
                {
                    slug: product.slug,
                    name: product.name,
                    price: product.price,
                    image: product.images?.[0],
                    quantity: qty,
                },
            ];
        });
        setOpen(true);
    };

    const remove = (slug) => setItems((prev) => prev.filter((p) => p.slug !== slug));
    const setQty = (slug, qty) =>
        setItems((prev) =>
            prev
                .map((p) => (p.slug === slug ? { ...p, quantity: Math.max(1, qty) } : p))
                .filter((p) => p.quantity > 0),
        );
    const clear = () => {
        setItems([]);
        setGiftBox(false);
    };

    const totals = useMemo(() => {
        const itemCount = items.reduce((s, i) => s + i.quantity, 0);
        const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
        // Tiered bundle discount: 5+ bars = 20% off, 3-4 bars = 15% off, else none.
        let bundleRate = 0;
        if (itemCount >= 5) bundleRate = 0.2;
        else if (itemCount >= 3) bundleRate = 0.15;
        const bundleDiscount = subtotal * bundleRate;
        const giftBoxFee = giftBox ? 8 : 0;
        const shipping = subtotal === 0 ? 0 : subtotal - bundleDiscount >= 80 ? 0 : 9.5;
        const total = subtotal - bundleDiscount + shipping + giftBoxFee;
        return { itemCount, subtotal, bundleRate, bundleDiscount, shipping, giftBoxFee, total };
    }, [items, giftBox]);

    const value = {
        items,
        open,
        setOpen,
        add,
        remove,
        setQty,
        clear,
        giftBox,
        setGiftBox,
        totals,
    };

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
